import { Injectable } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { GeoService } from './geo.service';
import { UtilityService } from './utility.service';
import {
  AddressSuggestion,
  SelectedAddressDetails,
  PlacesAutocompleteRequest,
  PlacesAutocompleteResponse,
  PlaceDetailsResponse,
} from '../models/google-places.model';

@Injectable({
  providedIn: 'root'
})
export class GooglePlacesService {

  private sessionToken: string = this.generateSessionToken();
  private lastRouteSelected: string | null = null;

  constructor(
    private apiService: ApiService,
    private geoService: GeoService,
    private utilityService: UtilityService,
  ) { }

  getSuggestions(input: string): Observable<AddressSuggestion[]> {
    const includedPrimaryTypes = ['street_address', 'route'];

    const body: PlacesAutocompleteRequest = {
      input: this.normalizeInput(input),
      includedPrimaryTypes,
      includedRegionCode: this.utilityService.country,
      languageCode: this.utilityService.country || 'nl',
      sessionToken: this.sessionToken,
    };

    return this.apiService.post('geo/places/autocomplete', body).pipe(
      map((response: PlacesAutocompleteResponse) => {
        if (!response.suggestions?.length) return [];
        const suggestions = response.suggestions
          .filter(s => s.placePrediction)
          .map(s => {
            const pred = s.placePrediction!;
            return {
              placeId: pred.placeId || '',
              mainText: pred.structuredFormat?.mainText?.text || pred.text?.text || '',
              secondaryText: pred.structuredFormat?.secondaryText?.text || '',
              fullText: pred.text?.text || '',
            } as AddressSuggestion;
          });
        return suggestions.filter(s => this.hasHouseNumber(s.mainText));
      }),
      catchError(() => of([] as AddressSuggestion[]))
    );
  }

  getPlaceDetails(placeId: string, userInput?: string): Observable<SelectedAddressDetails> {
    const fields = 'formattedAddress,addressComponents,location';
    const languageCode = this.utilityService.country || 'nl';
    const url = `geo/places/${placeId}?fields=${fields}&languageCode=${languageCode}&sessionToken=${this.sessionToken}`;

    return new Observable<SelectedAddressDetails>(subscriber => {
      this.apiService.get(url).subscribe({
        next: (response: PlaceDetailsResponse) => {
          const address = this.parseAddressDetails(response);

          // rearranged, and try to recover the street number from the user's input.
          // Keep the session alive while we're still resolving a route-level selection.
          if (address.route && !address.streetNumber) {
            this.lastRouteSelected = address.route;
            if (userInput) {
              const streetNumber = this.parseStreetNumber(userInput, address.route);
              if (streetNumber) {
                address.streetNumber = streetNumber;
              }
            }
          } else {
            // Got a complete address — end the session so the next lookup starts fresh.
            this.resetSession();
          }

          const hasCoordinates = !!(address.coordinates?.latitude && address.coordinates?.longitude);
          const postalCode = (address.postalCode || '').replace(/\s+/g, '');
          const isPostalCodeComplete = this.utilityService.isNL
            ? /^\d{4}[A-Za-z]{2}$/.test(postalCode)
            : /^\d{5}$/.test(postalCode);

          if (!isPostalCodeComplete && hasCoordinates) {
            this.geoService.getZipFromCoordinates(
              address.coordinates!.latitude,
              address.coordinates!.longitude
            ).pipe(
              catchError(() => of(null))
            ).subscribe(zipResponse => {
              if (zipResponse?.zip) {
                address.postalCode = zipResponse.zip;
              }
              subscriber.next(address);
              subscriber.complete();
            });
          } else {
            subscriber.next(address);
            subscriber.complete();
          }
        },
        error: (err) => subscriber.error(err)
      });
    });
  }

  resetSession(): void {
    this.sessionToken = this.generateSessionToken();
    this.lastRouteSelected = null;
  }

  private hasHouseNumber(mainText: string): boolean {
    return /\d+\s*[a-zA-Z]?$/.test(mainText.trim());
  }

  private normalizeInput(input: string): string {
    if (!this.lastRouteSelected) return input;

    const route = this.lastRouteSelected;
    const routeIndex = input.toLowerCase().indexOf(route.toLowerCase());
    if (routeIndex === -1) return input;

    const routeEndIndex = routeIndex + route.length;
    const afterRoute = input.substring(routeEndIndex);

    // If there's already a number right after the route, no rearrangement needed
    if (/^\s*\d/.test(afterRoute)) return input;

    const trailingNumberMatch = input.match(/(\d+[a-zA-Z]?)\s*$/);
    if (!trailingNumberMatch) return input;

    const houseNumber = trailingNumberMatch[1];
    const withoutTrailingNumber = input.replace(/\s*\d+[a-zA-Z]?\s*$/, '');

    return withoutTrailingNumber.substring(0, routeEndIndex)
      + ' ' + houseNumber
      + withoutTrailingNumber.substring(routeEndIndex);
  }

  /**
   * Fallback: when Google returns a route-level Place Details without a street number,
   * extract it from the user's input. Skips postal codes (NL: 4 digits + 2 letters, DE: 5 digits).
   */
  private parseStreetNumber(input: string, route: string): string | null {
    const routeIndex = input.toLowerCase().indexOf(route.toLowerCase());
    if (routeIndex === -1) return null;

    const withoutRoute = (input.substring(0, routeIndex) + input.substring(routeIndex + route.length)).trim();
    const withoutPostalCode = withoutRoute.replace(/\d{4}\s?[a-zA-Z]{2}/g, '').replace(/\b\d{5}\b/g, '');
    const match = withoutPostalCode.match(/(\d+[a-zA-Z]?)/);
    return match ? match[1] : null;
  }

  private generateSessionToken(): string {
    return crypto.randomUUID();
  }

  private parseAddressDetails(response: PlaceDetailsResponse): SelectedAddressDetails {
    const details: SelectedAddressDetails = {
      formattedAddress: response.formattedAddress || ''
    };

    if (response.addressComponents) {
      let postalCode = '';
      let postalCodeSuffix = '';
      response.addressComponents.forEach(component => {
        const types = component.types || [];
        if (types.includes('street_number')) details.streetNumber = component.longText;
        if (types.includes('route')) details.route = component.longText;
        if (types.includes('locality')) details.city = component.longText;
        if (types.includes('postal_code')) postalCode = component.longText || '';
        if (types.includes('postal_code_suffix')) postalCodeSuffix = component.longText || '';
        if (types.includes('administrative_area_level_1')) details.state = component.shortText;
        if (types.includes('country')) details.country = component.shortText;
      });
      details.postalCode = (postalCode + ' ' + postalCodeSuffix).trim();
    }

    if (response.location) {
      details.coordinates = {
        latitude: response.location.latitude || 0,
        longitude: response.location.longitude || 0,
      };
    }

    return details;
  }
}
